import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useAuditLog } from '../../hooks';
import { auditActionLabel, targetTypeLabel, ACTION_FILTER_GROUPS } from '../../lib/audit-labels';
import { toISODate } from '../../lib/thai-date';

interface AuditEntry {
  id: string;
  timestamp: string;
  actor_line_user_id: string | null;
  actor_user_id: string | null;
  actor_emp_code: string | null;
  actor_name: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  reason: string | null;
  level: 'INFO' | 'WARN' | 'ERROR';
}

interface AuditResponse {
  entries: AuditEntry[];
}

/** Content-only version used by the Settings page (tab: audit). */
export function OwnerAuditContent() {
  const [limit, setLimit] = useState(50);
  const [filterAction, setFilterAction] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const { data, isLoading } = useAuditLog(limit) as { data: AuditResponse | AuditEntry[] | undefined; isLoading: boolean };

  // Worker returns a bare array; older payloads used {entries:[...]}.
  // Note: `Array.prototype.entries` is a function, so plain `data?.entries`
  // would be truthy on an array — array check MUST come first.
  const entries: AuditEntry[] = Array.isArray(data)
    ? data
    : (data?.entries ?? []);

  const filtered = entries.filter((e) => {
    if (filterAction && e.action !== filterAction) return false;
    // 🚨 e.timestamp เป็น UTC แต่ช่องกรองกับตัวเลขบนจอเป็นวันไทย — ต้องแปลงก่อนเทียบ
    //    ไม่งั้นรายการตี 0 ครึ่งของไทยจะหลุดออกจากวันของตัวเอง ไปโผล่วันก่อนหน้า
    const dayThai = toISODate(new Date(e.timestamp));
    if (filterDateFrom && dayThai < filterDateFrom) return false;
    if (filterDateTo && dayThai > filterDateTo) return false;
    return true;
  });

  const levelBadge = (level: string) =>
    level === 'ERROR' ? 'badge-danger' : level === 'WARN' ? 'badge-warn' : 'badge-info';

  return (
    <>
      <div className="card p-3 mb-4 space-y-2">
        <div>
          <label className="label">ประเภทการทำงาน</label>
          <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)} className="input">
            <option value="">ทั้งหมด</option>
            {ACTION_FILTER_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.actions.map((a) => (
                  <option key={a} value={a}>{auditActionLabel(a)}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">ตั้งแต่</label>
            <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="input" />
          </div>
          <div>
            <label className="label">ถึง</label>
            <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="input" />
          </div>
        </div>
        <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="input">
          <option value={50}>50 รายการล่าสุด</option>
          <option value={100}>100 รายการล่าสุด</option>
          <option value={200}>200 รายการล่าสุด</option>
        </select>
      </div>

      {isLoading && <p className="text-center text-gray-500 py-4">กำลังโหลด...</p>}

      <div className="space-y-2">
        {filtered.map((e) => (
          <div key={e.id} className="card p-3">
            <div className="flex justify-between items-start mb-1">
              <span className="font-mono text-xs text-gray-500">
                {new Date(e.timestamp).toLocaleString('th-TH')}
              </span>
              <span className={levelBadge(e.level)}>{e.level}</span>
            </div>
            <p className="font-medium text-sm">{auditActionLabel(e.action)}</p>
            <p className="text-[10px] font-mono text-gray-400">{e.action}</p>
            {e.target_type && (
              <p className="text-xs text-gray-600">{targetTypeLabel(e.target_type)}: {e.target_id}</p>
            )}
            {(e.actor_name || e.actor_emp_code || e.actor_line_user_id) ? (
              <p className="text-xs text-gray-500">
                โดย:{' '}
                {e.actor_name ? (
                  <>
                    <span className="font-medium text-gray-700">{e.actor_name}</span>
                    {e.actor_emp_code && <span className="text-gray-400"> · {e.actor_emp_code}</span>}
                  </>
                ) : e.actor_emp_code ? (
                  <span className="font-mono text-gray-700">{e.actor_emp_code}</span>
                ) : (
                  <span className="font-mono text-gray-400 text-[11px]">{e.actor_line_user_id}</span>
                )}
              </p>
            ) : (
              <p className="text-xs text-gray-400 italic">โดย: ระบบ</p>
            )}
            {e.reason && <p className="text-xs text-gray-500 mt-1">{e.reason}</p>}
          </div>
        ))}
        {!isLoading && filtered.length === 0 && (
          <p className="text-center text-gray-500 py-8">ไม่มีข้อมูล</p>
        )}
      </div>
    </>
  );
}

/** Standalone page kept for legacy /owner/audit (now redirects via Settings). */
export function OwnerAudit() {
  return (
    <AdminLayout title="ประวัติการใช้งานระบบ">
      <OwnerAuditContent />
    </AdminLayout>
  );
}
